// ═══════════════════════════════════════════════════════════════
// Incident Hub — Global Activity Log
// Searchable list of all activity across incidents
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, TextInput, Stack,
} from '@mantine/core';
import {
  IconSearch, IconCirclePlus, IconRefresh, IconCircleMinus, IconLogin,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useIncidentStore } from '../../store/incidentStore';
import { primary, semantic } from '../../lib/colors';

const ACTION_ICONS: Record<string, { icon: React.ComponentType<{ size?: number; color?: string }>; color: string }> = {
  create: { icon: IconCirclePlus, color: '#07963F' },
  update: { icon: IconRefresh, color: primary[700] },
  delete: { icon: IconCircleMinus, color: semantic.error },
  check_in: { icon: IconLogin, color: '#16a34a' },
};

export default function ActivityLog() {
  const navigate = useNavigate();
  const { incidents } = useAuthStore();
  const { activityLog, loadActivityLog } = useIncidentStore();
  const [search, setSearch] = useState('');

  useEffect(() => { loadActivityLog(); }, []);

  const sorted = useMemo(() => {
    let list = [...activityLog].sort((a, b) => (b.datecreated || '').localeCompare(a.datecreated || ''));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        e.actorName.toLowerCase().includes(q) ||
        e.action.toLowerCase().includes(q) ||
        e.targetType.toLowerCase().includes(q) ||
        e.module.toLowerCase().includes(q) ||
        e.details.toLowerCase().includes(q)
      );
    }
    return list.slice(0, 100);
  }, [activityLog, search]);

  const getIncidentName = (incidentId: string) => {
    const inc = incidents.find(i => String(i.id) === incidentId);
    return inc?.name || `Incident #${incidentId}`;
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Activity Log</Title>
        <Text size="xs" c="dimmed">{sorted.length} entries</Text>
      </Group>

      <TextInput placeholder="Search by actor, action, module, details..."
        leftSection={<IconSearch size={14} />}
        value={search} onChange={e => setSearch(e.currentTarget.value)}
        size="xs" mb="md" />

      {sorted.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No activity found.</Text>
        </Paper>
      ) : (
        <Stack gap={4}>
          {sorted.map(entry => {
            const actionMeta = ACTION_ICONS[entry.action] || ACTION_ICONS.update;
            const ActionIcon = actionMeta.icon;
            let details = '';
            try { details = entry.details ? JSON.parse(entry.details)?.name || JSON.parse(entry.details)?.fields || '' : ''; } catch { details = entry.details; }
            return (
              <Paper key={entry.id} p="xs" radius="sm" withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => entry.incidentId && navigate(`/incidents/${entry.incidentId}`)}>
                <Group gap="xs" wrap="nowrap">
                  <ActionIcon size={14} color={actionMeta.color} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Group gap={6}>
                      <Text size="xs" fw={600}>{entry.actorName || 'System'}</Text>
                      <Text size="xs" c="dimmed">{entry.action} {entry.targetType}</Text>
                      {entry.module && (
                        <Badge size="xs" variant="light">{entry.module}</Badge>
                      )}
                    </Group>
                    <Group gap={6}>
                      <Text size="xs" c="dimmed" truncate>
                        {getIncidentName(entry.incidentId)}
                        {details ? ` — ${details}` : ''}
                      </Text>
                    </Group>
                  </div>
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                    {entry.datecreated ? new Date(entry.datecreated).toLocaleString() : ''}
                  </Text>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}
    </div>
  );
}
